import React from "react";
import Client from "../../models/client";
import { useForm } from "@mantine/form";
import { Alert, Box, Group, List, NumberInput, SimpleGrid, Text, TextInput, ThemeIcon, Title } from "@mantine/core";
import { InfoIcon, MapPinIcon, WrenchIcon } from "@phosphor-icons/react";
import TagCodeBlock from "../components/TagCodeBlock";

interface Props {
  client: Client
}

const replacePlaceholder = (text: string, placeholder: string, value: string | number) => {
  const s = String(value ?? '')
  return s === '' ? text : text.split(placeholder).join(s)
}

const CVTagContainer: React.FC<Props> = ({ client }) => {
  const serviceUse = String(client.invy_service_use)
  const showWeb = serviceUse === '0' || serviceUse === '2'

  const urlName = client.url_name

  const webForm = useForm<{
    parentDomain: string
    mi: string
    nm: string
    ta: string | number
    pt: string | number
    rm: string
    f1: string; f2: string; f3: string; f4: string; f5: string
  }>({
    initialValues: {
      parentDomain: '',
      mi: '', nm: '', ta: 1, pt: 1, rm: '1',
      f1: '', f2: '', f3: '', f4: '', f5: '',
    },
  })
  const v = webForm.values

  const cookieTagBase = `<script type="text/javascript">
function getParam(name, url) {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, "\\$&");
  var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
      results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\\+/g, " "));
}

var tokey=getParam('tokey');
var suid=getParam('suid');
var code=getParam('code');

if(tokey){
    document.cookie = 'invy_tk='+tokey+'; path=/; max-age=31536000; domain=#親ドメインを記載してください#';
}
if(suid){
    document.cookie = 'invy_suid='+suid+'; path=/; max-age=31536000; domain=#親ドメインを記載してください#';
}
if(code){
    document.cookie = 'invy_code='+code+'; path=/; max-age=31536000; domain=#親ドメインを記載してください#';
}
</script>`

  const cvTagBase = `<script type="text/javascript">
  function getCookie(name) {
      var matches = document.cookie.match(new RegExp(
          "(?:^|; )" + name.replace(/([\\.$?*|{}\\(\\)\\[\\]\\\\\\/\\+^])/g, '\\\\$1') + "=([^;]*)"
      ));
      return matches ? decodeURIComponent(matches[1]) : undefined;
  }

  var p = { ci:'${urlName}',mi:'#被紹介者ID#', nm:'#被紹介者名#', ta:'#売上金額#', pt:'#成果ポイント#', rm:'#備考#', f1:'#汎用項目1#', f2:'#汎用項目2#', f3:'#汎用項目3#', f4:'#汎用項目4#', f5:'#汎用項目5#', tokey: getCookie('invy_tk'), suid: getCookie('invy_suid'), code: getCookie('invy_code') };
  var s = document.createElement("img");
  s.async = true;
  s.src = encodeURI('//${window?.location?.hostname || 'invy.jp'}/rest/report/addcv?ci='+p.ci+'&ta='+p.ta+'&pt='+p.pt+'&tokey='+p.tokey+'&suid='+p.suid+'&code='+p.code)
    +'&mi='+encodeURIComponent(p.mi)
    +'&nm='+encodeURIComponent(p.nm)
    +'&rm='+encodeURIComponent(p.rm)
    +'&f1='+encodeURIComponent(p.f1)
    +'&f2='+encodeURIComponent(p.f2)
    +'&f3='+encodeURIComponent(p.f3)
    +'&f4='+encodeURIComponent(p.f4)
    +'&f5='+encodeURIComponent(p.f5);
  var s0 = document.getElementsByTagName('script')[0];
  s0.parentNode.insertBefore(s, s0);
</script>`

  const cookieTag = replacePlaceholder(cookieTagBase, '#親ドメインを記載してください#', v.parentDomain)

  let cvTag = cvTagBase
  cvTag = replacePlaceholder(cvTag, '#被紹介者ID#', v.mi)
  cvTag = replacePlaceholder(cvTag, '#被紹介者名#', v.nm)
  cvTag = replacePlaceholder(cvTag, '#売上金額#', v.ta)
  cvTag = replacePlaceholder(cvTag, '#成果ポイント#', v.pt)
  cvTag = replacePlaceholder(cvTag, '#備考#', v.rm)
  cvTag = replacePlaceholder(cvTag, '#汎用項目1#', v.f1)
  cvTag = replacePlaceholder(cvTag, '#汎用項目2#', v.f2)
  cvTag = replacePlaceholder(cvTag, '#汎用項目3#', v.f3)
  cvTag = replacePlaceholder(cvTag, '#汎用項目4#', v.f4)
  cvTag = replacePlaceholder(cvTag, '#汎用項目5#', v.f5)

  return (
    <Box>
      <Group gap={12} mb={40}>
        <ThemeIcon variant="transparent" color="#5DA327">
          <WrenchIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          計測タグ設定
        </Title>
      </Group>

      {showWeb && (
        <>
          <Box mb={80}>
            <Title order={2} size={18} fw={600} mb={16}>
              invyから遷移した最初のページに設置いただくタグ
            </Title>

            <TagCodeBlock code={cookieTag} />

            <Box p={16} bdrs={8} mt={32} style={{ border: '1px solid #0000001A' }}>
              <Text fz={16} fw={600} c="#000000CC" mb={14}>差込値</Text>
              <TextInput
                label="親ドメイン"
                placeholder="例：invy.co.jp（https://www. は不要）"
                {...webForm.getInputProps('parentDomain')}
              />
            </Box>

            <Alert color="gray" icon={<MapPinIcon size={16} />} variant="light" mt={12}>
              <Text fz={13} fw={600} c="#000000CC">設置場所</Text>
              <Text fz={13} c="#00000099">
                invyのguestページから遷移した先の貴社ウェブページの&lt;body&gt;内に設置してください。
              </Text>
            </Alert>

            <Alert color="blue" icon={<InfoIcon size={16} />} variant="light" mt={12}>
              <Text fz={13} c="#00000099">
                上のタグは、「差込値」の親ドメインを入力するとリアルタイムに反映されます。
                フォームを使わず直接タグを編集する場合は、
                <Text component="span" fz={13} fw={600} c="#000000CC">
                  #親ドメインを記載してください#
                </Text>
                を親ドメイン（例：invy.co.jp）に置き換えてください。
              </Text>
            </Alert>
          </Box>

          <Box>
            <Title order={2} size={18} fw={600} mb={16}>CVタグ</Title>

            <TagCodeBlock code={cvTag} />

            <Box p={16} bdrs={8} mt={32} style={{ border: '1px solid #0000001A' }}>
              <Text fz={16} fw={600} c="#000000CC" mb={14}>差込値</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput label="被紹介者ID" {...webForm.getInputProps('mi')} />
                <TextInput label="被紹介者名" {...webForm.getInputProps('nm')} />
                <NumberInput
                  label="売上金額"
                  hideControls
                  allowDecimal={false}
                  allowNegative={false}
                  min={0}
                  {...webForm.getInputProps('ta')}
                />
                <NumberInput
                  label="成果ポイント"
                  hideControls
                  allowDecimal={false}
                  allowNegative={false}
                  min={0}
                  {...webForm.getInputProps('pt')}
                />
                <TextInput label="備考" {...webForm.getInputProps('rm')} />
                <TextInput label="汎用項目1" {...webForm.getInputProps('f1')} />
                <TextInput label="汎用項目2" {...webForm.getInputProps('f2')} />
                <TextInput label="汎用項目3" {...webForm.getInputProps('f3')} />
                <TextInput label="汎用項目4" {...webForm.getInputProps('f4')} />
                <TextInput label="汎用項目5" {...webForm.getInputProps('f5')} />
              </SimpleGrid>
            </Box>

            <Alert color="gray" icon={<MapPinIcon size={16} />} variant="light" mt={16}>
              <Text fz={13} fw={600} c="#000000CC">設置場所</Text>
              <Text fz={13} c="#00000099">
                invyのguestページから遷移した先の貴社ウェブページ（申し込み完了などの成果を測定したいページ）の&lt;body&gt;内に設置してください。
              </Text>
            </Alert>

            <Alert color="blue" icon={<InfoIcon size={16} />} variant="light" mt={16}>
              <Text fz={13} fw={600} c="#000000CC" mb={8}>差込項目について</Text>
              <Text fz={13} c="#00000099" mb={8}>
                上の「差込値」に値を入れるとタグに反映されます（空欄のままなら差込用のプレースホルダを維持します）。
              </Text>
              <List fz={13} c="#00000099" spacing={8}>
                <List.Item>
                  #被紹介者ID# には被紹介者（お友達）を特定するためのIDを付与ください。識別子、問い合わせ番号、メールアドレスなど、guestを特定できるものであれば問題ございません。 ※255文字までの制限がございます。
                </List.Item>
                <List.Item>
                  #被紹介者名# には被紹介者（お友達）のお名前を入れていただくことができます（任意）。分析画面の「友人 名前（WEB）」列に表示され、通知メールの変数「ゲストの名前」に差し込まれます。貴社の運用上不要であれば空欄のままで問題ございません。 ※255文字までの制限がございます。
                </List.Item>
                <List.Item>
                  #売上金額# には被紹介者（お友達）のCV金額を入れていただくことができます（任意）。ただし上記は必須ではございませんので、貴社の運用上不要であれば 1 など固定値を挿入ください。 ※数値のみ挿入可能です。
                </List.Item>
                <List.Item>
                  #成果ポイント# には複数の成果ポイントがあった場合の識別番号を入れていただくことができます。成果ポイントが一か所であれば 1 を挿入ください。 ※数値のみ挿入可能です。
                </List.Item>
                <List.Item>
                  #備考# にはどんな値でも入れていただくことができます（任意）。ただし上記は必須ではございませんので、貴社の運用上不要であれば 1 など固定値を挿入ください。 例）体験参加日時など ※全角文字で3万文字程度まで挿入可能です。
                </List.Item>
                <List.Item>
                  #汎用項目1#～#汎用項目5# には任意の値を入れていただくことができます（すべて任意）。分析画面の「友人 汎用項目1（WEB）」～「友人 汎用項目5（WEB）」列に表示されます。漢字・ひらがな・カタカナ・英字・数字いずれも格納可能です。貴社の運用上不要な項目は空欄のままで問題ございません。 ※各項目255文字までの制限がございます。
                </List.Item>
              </List>
            </Alert>
          </Box>
        </>
      )}
    </Box>
  )
}

export default CVTagContainer
